<script>
  // KeyWorkspace — the per-tab area showing Generator | Usage | History.
  // Each instance of this component owns its own state (the `tab` prop)
  // so switching tabs preserves everything: running jobs, file uploads,
  // history page, etc.
  import GeneratorPanel from "./GeneratorPanel.svelte";
  import UsagePanel     from "./UsagePanel.svelte";
  import HistoryPanel   from "./HistoryPanel.svelte";
  import { getMode, tabs as tabStore, setActiveTabId, createTab } from "../stores/app.svelte.js";

  let { tab, keys } = $props();

  // In SINGLE mode the tab bar is hidden, so on first mount we pin the
  // workspace to the first available stored key (if any). This way the
  // user always sees a real-key workspace without needing to manage tabs.
  const mode = $derived(getMode());
  $effect(() => {
    if (mode === "single" && tab?.isDefault && keys?.length > 0) {
      // Replace the default placeholder with the first stored key.
      const idx = tabStore.findIndex((t) => t.keyId === "default");
      if (idx !== -1) {
        tabStore.splice(idx, 1, createTab(keys[0]));
        setActiveTabId(keys[0].id);
      }
    }
  });
</script>

<div class="workspace-inner">
  <!-- Inner panel switcher -->
  <div class="panel-tabs">
    <button class:active={tab.activePanel === "generator"}
            onclick={() => (tab.activePanel = "generator")}>
      🎨 Generator
    </button>
    <button class:active={tab.activePanel === "usage"}
            onclick={() => (tab.activePanel = "usage")}>
      💰 Usage
    </button>
    <button class:active={tab.activePanel === "history"}
            onclick={() => (tab.activePanel = "history")}>
      📊 Transactions
    </button>

    <div class="panel-tabs-meta">
      <span class="key-name">{tab.label}</span>
      <span class="key-mask">{tab.maskedKey}</span>
    </div>
  </div>

  <!-- Panel content — render only the active one to save DOM nodes.
       (Jobs is no longer a per-tab panel — it lives in the header
       modal because job history is global, not per-key.) -->
  {#if tab.activePanel === "generator"}
    <GeneratorPanel {tab} />
  {:else if tab.activePanel === "usage"}
    <UsagePanel {tab} />
  {:else if tab.activePanel === "history"}
    <HistoryPanel {tab} />
  {/if}
</div>

<style>
  .workspace-inner {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .panel-tabs {
    display: flex;
    gap: 4px;
    border-bottom: 1px solid var(--border);
    align-items: center;
    padding-bottom: 0;
  }

  .panel-tabs button {
    padding: 8px 14px;
    border-radius: 6px 6px 0 0;
    font-size: 0.85rem;
    color: var(--muted);
    font-weight: 500;
    transition: background 0.15s, color 0.15s;
    margin-bottom: -1px;
    border: 1px solid transparent;
    border-bottom: none;
  }

  .panel-tabs button:hover {
    color: var(--text);
    background: var(--panel-2);
  }

  .panel-tabs button.active {
    color: var(--text-strong);
    background: var(--panel);
    border-color: var(--border);
  }

  .panel-tabs-meta {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 6px;
    font-size: 0.78rem;
    color: var(--muted);
  }

  .key-name {
    color: var(--text-strong);
    font-weight: 500;
  }

  .key-mask {
    font-family: ui-monospace, monospace;
    font-size: 0.72rem;
    padding: 2px 6px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 4px;
  }
</style>
